<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import QRCode from 'qrcode';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import wahaApi from './api';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();
const waha = ref({ ...(props.inbox.waha || {}) });
const busy = ref(false);
const qrSrc = ref(null);
let statusTimer = null;

const CONNECTION_KEYS = [
  'session',
  'app_id',
  'session_status',
  'connected',
  'synced_at',
  'phone',
];

const TRANSIENT_STATUSES = ['STARTING', 'STOPPED', 'FAILED'];

const connected = computed(() => !!waha.value.connected);

const formattedPhone = computed(() => {
  const raw = String(waha.value.phone || '').trim();
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length >= 12 && digits.startsWith('55')) {
    const ddd = digits.slice(2, 4);
    const number = digits.slice(4);
    if (number.length === 9) {
      return `+55 ${ddd} ${number.slice(0, 5)}-${number.slice(5)}`;
    }
    if (number.length === 8) {
      return `+55 ${ddd} ${number.slice(0, 4)}-${number.slice(4)}`;
    }
  }
  return raw.startsWith('+') ? raw : `+${digits || raw}`;
});

const resolveQrSrc = async payload => {
  if (!payload) return null;
  if (typeof payload === 'string') {
    if (payload.startsWith('data:')) return payload;
    return QRCode.toDataURL(payload, { width: 240, margin: 1 });
  }
  if (payload.data) {
    const mime = payload.mimetype || 'image/png';
    return `data:${mime};base64,${payload.data}`;
  }
  if (payload.value) {
    return QRCode.toDataURL(payload.value, { width: 240, margin: 1 });
  }
  return null;
};

const setQrPayload = async payload => {
  qrSrc.value = await resolveQrSrc(payload);
};

const connectionFrom = wahaData => {
  const connection = {};
  CONNECTION_KEYS.forEach(key => {
    if (key in wahaData) connection[key] = wahaData[key];
  });
  return connection;
};

const patchInboxStore = connection => {
  if (!props.inbox?.id || !Object.keys(connection).length) return;

  store.dispatch('inboxes/updateWahaConnection', {
    id: props.inbox.id,
    waha: connection,
    reauthorizationRequired: !waha.value.connected,
  });
};

const applyConnection = wahaData => {
  if (!wahaData) return;
  const connection = connectionFrom(wahaData);
  waha.value = { ...waha.value, ...connection };
  patchInboxStore(connection);
};

const stopStatusPolling = () => {
  if (statusTimer) clearInterval(statusTimer);
  statusTimer = null;
};

const sessionStatus = () => (waha.value.session_status || '').toUpperCase();

const syncStatus = async () => {
  const { data } = await wahaApi.sessionStatus(props.inbox.id);
  applyConnection(data.waha || {});
  return !!data.waha?.connected;
};

const fetchQr = async () => {
  const { data: qrData } = await wahaApi.qr(props.inbox.id);
  await setQrPayload(qrData.qr || null);
};

const fetchQrWithRetry = async (attempts = 5) => {
  let lastError;
  for (let i = 0; i < attempts; i += 1) {
    try {
      // Sequential retries until WAHA returns a scannable QR.
      // eslint-disable-next-line no-await-in-loop
      await fetchQr();
      if (qrSrc.value) return;
    } catch (error) {
      lastError = error;
    }
    // eslint-disable-next-line no-await-in-loop
    if (await syncStatus()) return;
    // eslint-disable-next-line no-await-in-loop
    await new Promise(resolve => {
      setTimeout(resolve, 1200);
    });
  }
  if (lastError) throw lastError;
  throw new Error(t('INBOX_MGMT.ADD.WHATSAPP.WAHA.LINK.QR_WAITING'));
};

const startStatusPolling = () => {
  stopStatusPolling();
  if (connected.value) return;
  statusTimer = setInterval(async () => {
    try {
      if (await syncStatus()) {
        qrSrc.value = null;
        stopStatusPolling();
        return;
      }
      const status = sessionStatus();
      if (status === 'SCAN_QR_CODE') {
        try {
          await fetchQr();
        } catch {
          // keep waiting; user can refresh
        }
      }
    } catch {
      // retry on next poll
    }
  }, 3000);
};

const refreshConnection = async () => {
  if (!props.inbox?.id || busy.value) return;
  busy.value = true;
  qrSrc.value = null;
  try {
    if (await syncStatus()) {
      stopStatusPolling();
      return;
    }
    await fetchQrWithRetry();
    startStatusPolling();
  } catch (error) {
    useAlert(error?.response?.data?.error || error.message);
    startStatusPolling();
  } finally {
    busy.value = false;
  }
};

const storeInbox = computed(() =>
  store.getters['inboxes/getInbox'](props.inbox.id)
);

watch(
  () => storeInbox.value?.waha?.connected,
  (isConnected, wasConnected) => {
    const next = storeInbox.value?.waha;
    if (!next) return;
    waha.value = { ...waha.value, ...connectionFrom(next) };
    if (isConnected) {
      qrSrc.value = null;
      stopStatusPolling();
      return;
    }
    if (wasConnected && !isConnected) {
      const status = (next.session_status || '').toUpperCase();
      if (TRANSIENT_STATUSES.includes(status) || status === 'SCAN_QR_CODE') {
        startStatusPolling();
        return;
      }
      refreshConnection();
    }
  }
);

onMounted(() => {
  if (connected.value) {
    syncStatus();
    return;
  }
  refreshConnection();
});

onUnmounted(stopStatusPolling);
</script>

<template>
  <div>
    <div
      v-if="connected"
      class="flex max-w-md items-center gap-3 rounded-xl border border-n-teal-6/50 bg-n-teal-3/50 px-4 py-3"
    >
      <span class="relative flex size-2.5 shrink-0">
        <span
          class="absolute inline-flex size-full animate-ping rounded-full bg-n-teal-9 opacity-35"
        />
        <span class="relative inline-flex size-2.5 rounded-full bg-n-teal-9" />
      </span>
      <div class="flex min-w-0 flex-col gap-0.5">
        <span class="text-sm font-medium text-n-teal-11">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.WAHA.STATUS.CONNECTED') }}
        </span>
        <span
          v-if="formattedPhone"
          class="truncate text-sm tabular-nums text-n-slate-12"
        >
          {{ formattedPhone }}
        </span>
      </div>
    </div>

    <div
      v-else
      class="flex max-w-xl flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-4 sm:flex-row sm:items-start"
    >
      <div
        class="relative mx-auto flex size-56 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white p-2 shadow-sm outline outline-1 -outline-offset-1 outline-n-weak sm:mx-0"
      >
        <img
          v-if="qrSrc"
          :src="qrSrc"
          :alt="$t('INBOX_MGMT.ADD.WHATSAPP.WAHA.STATUS.WAITING_QR')"
          class="size-full object-contain"
        />
        <div v-else class="flex flex-col items-center gap-2">
          <Spinner :size="28" class="text-n-slate-10" />
          <span class="text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.WAHA.LINK.QR_WAITING') }}
          </span>
        </div>
      </div>

      <div class="flex min-w-0 flex-1 flex-col gap-3">
        <div>
          <p class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.WAHA.STATUS.WAITING_QR') }}
          </p>
          <ol class="mt-2 space-y-1.5 text-xs leading-5 text-n-slate-11">
            <li class="flex gap-2">
              <span
                class="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-n-alpha-2 text-[11px] font-medium text-n-slate-12"
              >
                {{ 1 }}
              </span>
              <span>
                {{ $t('INBOX_MGMT.ADD.WHATSAPP.WAHA.SETTINGS.QR_STEPS.ONE') }}
              </span>
            </li>
            <li class="flex gap-2">
              <span
                class="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-n-alpha-2 text-[11px] font-medium text-n-slate-12"
              >
                {{ 2 }}
              </span>
              <span>
                {{ $t('INBOX_MGMT.ADD.WHATSAPP.WAHA.SETTINGS.QR_STEPS.TWO') }}
              </span>
            </li>
            <li class="flex gap-2">
              <span
                class="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-n-alpha-2 text-[11px] font-medium text-n-slate-12"
              >
                {{ 3 }}
              </span>
              <span>
                {{ $t('INBOX_MGMT.ADD.WHATSAPP.WAHA.SETTINGS.QR_STEPS.THREE') }}
              </span>
            </li>
          </ol>
        </div>
        <div>
          <NextButton
            size="sm"
            variant="faded"
            color="slate"
            icon="i-lucide-refresh-cw"
            :label="$t('INBOX_MGMT.ADD.WHATSAPP.WAHA.SETTINGS.REFRESH_QR')"
            :is-loading="busy"
            :disabled="busy"
            @click="refreshConnection"
          />
        </div>
      </div>
    </div>
  </div>
</template>
